/**
 * SakTidslinje - Oversikt over saker sortert etter juridisk stadium
 *
 * Stadier:
 * 1) Oppdrag - Innledende fase
 * 2) Juridisk vurdering
 * 3) Spesifisert krav
 * 4) Søksmålsvarsel
 * 5) Søksmål
 * 6) Planmøte
 * 7) Rettsmekling
 * 8) Sluttinnlegg
 * 9) Hovedforhandling
 * 10) Dom
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './SakTidslinje.css';

const FASER = [
  { id: 1, navn: 'Oppdrag', ikon: '📋', beskrivelse: 'Innledende fase' },
  { id: 2, navn: 'Juridisk vurdering', ikon: '⚖️', beskrivelse: 'Vurdering av saken' },
  { id: 3, navn: 'Spesifisert krav', ikon: '📄', beskrivelse: 'Krav spesifisert' },
  { id: 4, navn: 'Søksmålsvarsel', ikon: '⚠️', beskrivelse: 'Varsel sendt' },
  { id: 5, navn: 'Søksmål', ikon: '📨', beskrivelse: 'Søksmål innlevert' },
  { id: 6, navn: 'Planmøte', ikon: '📅', beskrivelse: 'Planlegging i retten' },
  { id: 7, navn: 'Rettsmekling', ikon: '🤝', beskrivelse: 'Mekling i retten' },
  { id: 8, navn: 'Sluttinnlegg', ikon: '📝', beskrivelse: 'Sluttinnlegg levert' },
  { id: 9, navn: 'Hovedforhandling', ikon: '⚖️', beskrivelse: 'Hovedforhandling' },
  { id: 10, navn: 'Dom', ikon: '🏛️', beskrivelse: 'Dom avsagt' }
];

export default function SakTidslinje({ navigerTil }) {
  const [saker, setSaker] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visningstype, setVisningstype] = useState('kanban'); // 'kanban' | 'tidslinje'
  const [redigerSak, setRedigerSak] = useState(null);

  useEffect(() => {
    lastSaker();
  }, []);

  const lastSaker = async () => {
    try {
      setLoading(true);
      const response = await crmApi.getSaker({ status: 'aktiv' });
      const sakerData = response.saker || [];
      setSaker(sakerData);
    } catch (error) {
      console.error('Feil ved lasting av saker:', error);
      setSaker([]);
    } finally {
      setLoading(false);
    }
  };

  const oppdaterFase = async (sakId, nyFaseId) => {
    try {
      await crmApi.request(`/api/saker/${sakId}`, {
        method: 'PUT',
        body: JSON.stringify({ current_phase_id: nyFaseId })
      });

      // Oppdater lokal state
      setSaker(saker.map(s => s.id === sakId ? { ...s, current_phase_id: nyFaseId } : s));
      alert('✅ Fase oppdatert!');
    } catch (error) {
      console.error('Feil ved oppdatering av fase:', error);
      alert('Kunne ikke oppdatere fase');
    }
  };

  const getSakerForFase = (faseId) => {
    return saker.filter(s => (s.current_phase_id || 1) === faseId);
  };

  const handleDragStart = (e, sak) => {
    e.dataTransfer.setData('sakId', sak.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, faseId) => {
    e.preventDefault();
    const sakId = e.dataTransfer.getData('sakId');
    oppdaterFase(sakId, faseId);
  };

  if (loading) {
    return (
      <div className="sak-tidslinje loading">
        <div className="spinner"></div>
        <p>Laster tidslinje...</p>
      </div>
    );
  }

  return (
    <div className="sak-tidslinje">
      <div className="tidslinje-header">
        <h1>📊 Saktidslinje - Juridiske stadier</h1>
        <div className="tidslinje-actions">
          <button
            className={visningstype === 'kanban' ? 'btn-active' : 'btn-secondary'}
            onClick={() => setVisningstype('kanban')}
          >
            🗂️ Kanban
          </button>
          <button
            className={visningstype === 'tidslinje' ? 'btn-active' : 'btn-secondary'}
            onClick={() => setVisningstype('tidslinje')}
          >
            📈 Tidslinje
          </button>
        </div>
      </div>

      <p className="tidslinje-info">
        Totalt {saker.length} aktive saker • Dra og slipp for å flytte mellom stadier
      </p>

      {visningstype === 'kanban' ? (
        <div className="kanban-board">
          {FASER.map(fase => {
            const sakerIFase = getSakerForFase(fase.id);
            return (
              <div
                key={fase.id}
                className="kanban-kolonne"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, fase.id)}
              >
                <div className="kolonne-header">
                  <h3>
                    {fase.ikon} {fase.navn}
                  </h3>
                  <span className="antall-badge">{sakerIFase.length}</span>
                </div>
                <p className="fase-beskrivelse">{fase.beskrivelse}</p>

                <div className="sak-liste">
                  {sakerIFase.length === 0 ? (
                    <div className="empty-kolonne">
                      <p>Ingen saker</p>
                    </div>
                  ) : (
                    sakerIFase.map(sak => (
                      <div
                        key={sak.id}
                        className="sak-kort"
                        draggable
                        onDragStart={(e) => handleDragStart(e, sak)}
                        onClick={() => navigerTil && navigerTil('sak-detaljer', sak.id)}
                      >
                        <div className="sak-kort-header">
                          <strong>{sak.saksnummer}</strong>
                          <span className={`prioritet-badge prioritet-${sak.prioritet}`}>
                            {sak.prioritet || 'middels'}
                          </span>
                        </div>
                        <h4>{sak.tittel || sak.klient_navn}</h4>
                        <p className="sak-type">{sak.sakstype}</p>
                        <div className="sak-meta">
                          <span>👤 {sak.klient_navn}</span>
                          {sak.motpart_navn && (
                            <span>⚔️ {sak.motpart_navn}</span>
                          )}
                        </div>
                        <div className="sak-footer">
                          <button
                            className="btn-endre-fase"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRedigerSak(sak);
                            }}
                          >
                            ⚙️
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="tidslinje-view">
          <div className="tidslinje-akse">
            {FASER.map((fase, index) => {
              const sakerIFase = getSakerForFase(fase.id);
              return (
                <div key={fase.id} className="tidslinje-fase">
                  <div className="fase-punkt">
                    <div className="punkt-ikon">{fase.ikon}</div>
                    <div className={`punkt-sirkel ${sakerIFase.length > 0 ? 'aktiv' : ''}`}>
                      {sakerIFase.length}
                    </div>
                  </div>
                  <div className="fase-innhold">
                    <h3>{fase.navn}</h3>
                    <p>{fase.beskrivelse}</p>
                    {sakerIFase.length > 0 && (
                      <ul className="fase-saker">
                        {sakerIFase.map(sak => (
                          <li
                            key={sak.id}
                            onClick={() => navigerTil && navigerTil('sak-detaljer', sak.id)}
                          >
                            <strong>{sak.saksnummer}</strong> - {sak.tittel || sak.klient_navn}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {index < FASER.length - 1 && <div className="tidslinje-linje"></div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Endre fase modal */}
      {redigerSak && (
        <div className="modal-overlay" onClick={() => setRedigerSak(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Endre juridisk stadium</h3>
            <p>
              <strong>{redigerSak.saksnummer}</strong> - {redigerSak.tittel || redigerSak.klient_navn}
            </p>
            <div className="fase-velger">
              {FASER.map(fase => (
                <button
                  key={fase.id}
                  className={`fase-knapp ${(redigerSak.current_phase_id || 1) === fase.id ? 'aktiv' : ''}`}
                  onClick={() => {
                    oppdaterFase(redigerSak.id, fase.id);
                    setRedigerSak(null);
                  }}
                >
                  {fase.ikon} {fase.navn}
                </button>
              ))}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setRedigerSak(null)}>
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
